import { Component, OnInit, OnDestroy } from '@angular/core';
import { Datasource as DatasourceCtor } from 'ngx-ui-scroll';
import { of } from 'rxjs';
import { take, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IPrefChangeSummary } from '../../interfaces/stats/pref-change-summary';
import { IPrefChangeDetails } from '../../interfaces/stats/pref-change-details';
import { IPrefChangeDetailsChunkResponse } from '../../interfaces/stats/pref-change-details-chunk-response';
import { ClientStatsSectionBase } from '../client-stats-section-base';

@Component({
  selector: 'app-client-stats-pref-changes',
  standalone: false,
  templateUrl: './client-stats-pref-changes.component.html',
  styleUrl: './client-stats-pref-changes.component.css'
})
export class ClientStatsPrefChangesComponent
  extends ClientStatsSectionBase<IPrefChangeDetails>
  implements OnInit, OnDestroy {

  // ── Pref-changes-specific state ───────────────────────────────────────────
  totalAccounts    = 0;
  totalChanges     = 0;
  // totalUniquePrefs = 0;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override ngOnInit(): void {
    this.sortField       = 'pref_change_id';
    this.pdfSectionTitle = 'Preference Changes';
    this.chartOptions = this.sharedService.options('Preference Changes by Name');

    this.summaryColumns = [
      { field: 'prefName',      header: 'Preference Name', width: '50%', display: 'table-cell' },
      { field: 'accountCnt',    header: 'Account Count',   width: '25%', display: 'table-cell' },
      { field: 'prefChangeCnt', header: 'Change Count',    width: '25%', display: 'table-cell' },
    ];

    this.initDatasource();
  }

  override ngOnDestroy(): void {}

  // ── Abstract implementations ──────────────────────────────────────────────

  override isSelected(row: IPrefChangeDetails): boolean {
    return this.selectedRow?.prefChangeId === row.prefChangeId;
  }

  override fetchData(): void {
    this.customLoggerService.logDebug('Fetching pref changes summary - Brand:', this.selectedBrandCode,
      'Date From:', this.selectedDateFrom, 'Date To:', this.selectedDateTo);

    if (!(this.selectedBrandCode && this.selectedDateFrom && this.selectedDateTo)) {
      this.showMissingCriteriaWarning();
      return;
    }

    this.isSpinnerHidden = false;
    this.hasData         = false;

    this.clientStatsService
      .getPrefChangesSummary(this.baseUrl, this.selectedBrandCode, this.selectedDateFrom, this.selectedDateTo)
      .subscribe({
        next: (result: IPrefChangeSummary[]) => {
          this.customLoggerService.logDebug('Pref changes summary result:', result);
          this.isSpinnerHidden = true;

          if (result.length === 0) {
            this.summaryRows = [];
            this.chartData   = { labels: [], datasets: [] };
            this.showNoDataWarning();
            return;
          }

          // Totals
          result.forEach(r => {
            this.totalAccounts   += Number(r.accountCnt);
            this.totalChanges     += Number(r.prefChangeCnt);
            // this.totalUniquePrefs  += Number(r.prefChangeCnt);
          });
          this.summaryRows = result;

          const labels        = result.map(r => r.prefName);
          const prefChangeCnts = result.map(r => r.prefChangeCnt);
          const accountCnts   = result.map(r => r.accountCnt);

          this.chartData = {
            labels,
            datasets: [
              { label: 'Change Count',  backgroundColor: '#2726dd',  borderColor: '#2726dd',  data: prefChangeCnts },
              { label: 'Account Count', backgroundColor: '#2AFFA2', borderColor: '#2AFFA2', data: accountCnts }
            ]
          };
          this.hasData = true;
          this.refreshChart();
        },
        error: (error: unknown) => {
          this.isSpinnerHidden = true;
          this.customLoggerService.logError('Error fetching pref changes summary', error);
        }
      });
  }

  override initDatasource(): void {
    this.filterName$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.currentFilterName = value ?? '';
        this.pagination.clear();
        this.datasource?.adapter.reload(0);
      });

    this.datasource = new DatasourceCtor<IPrefChangeDetails>({
      get: (index: number, count: number, success: (items: IPrefChangeDetails[]) => void) => {
        this.customLoggerService.logDebug(`Requesting block: index=${index}, count=${count}, sort=${this.sortField} dir=${this.sortDirection}`);
        if (index < 0) { success([]); return; }

        const dir        = this.sortDirection;
        const blockSize  = count;
        const blockStart = Math.floor(index / blockSize) * blockSize;
        const startId    = this.pagination.resolveStartId(blockStart, dir, blockSize);
        this.customLoggerService.logDebug(`Resolved startId=${startId} for blockStart=${blockStart}, dir=${dir}`);

        if (startId == null) { success([]); return; }

        this.clientStatsService
          .getPrefChangesDetails(
            this.baseUrl, this.selectedBrandCode,
            this.selectedDateFrom, this.selectedDateTo,
            startId, blockSize, dir, this.sortField)
          .pipe(
            take(1),
            catchError((err: unknown) => {
              console.error('chunk error:', err);
              const empty: IPrefChangeDetailsChunkResponse = {
                summaryStatistics: { totalCnt: 0, minId: 0, maxId: 0 },
                prefChangeDescList: []
              };
              return of(empty);
            })
          )
          .subscribe((resp: IPrefChangeDetailsChunkResponse) => {
            this.customLoggerService.logDebug(`Received chunk - totalCnt: ${resp.summaryStatistics.totalCnt}, items: ${resp.prefChangeDescList.length}`);
            this.pagination.updateMeta(
              resp.summaryStatistics.totalCnt,
              resp.summaryStatistics.minId,
              resp.summaryStatistics.maxId
            );

            const rows: IPrefChangeDetails[] = (resp.prefChangeDescList ?? []).map(r => ({
              prefChangeId: r.prefChangeId,
              createDate:   r.createDate,
              extAccountId: r.extAccountId,
              prefName:     r.prefName,
              prevValue:    r.prevValue,
              curValue:     r.curValue
            }));

            if (rows.length > 0) {
              const lastId = rows[rows.length - 1]?.prefChangeId ?? 0;
              this.pagination.cacheNextBlock(dir, blockStart, blockSize, lastId);
            }
            success(rows);
          });
      },
      settings: {
        startIndex: 0,
        minIndex:   0,
        bufferSize: this.pagination.bufferSize,
        padding:    this.pagination.padding
      }
    });
  }
}
